/**
 * @file Diagnostic page to test Firebase connectivity - Protected by password
 */
import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, enableNetwork, disableNetwork } from 'firebase/firestore';

const DiagnosticPage: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isChecking, setIsChecking] = useState<boolean>(true);
    const [status, setStatus] = useState<string>('Ready');
    const [logs, setLogs] = useState<string[]>([]);

    useEffect(() => {
        // Verificar si ya está autenticado en esta sesión
        const isAuth = sessionStorage.getItem('diagnostic_auth') === 'true';
        if (isAuth) {
            setIsAuthenticated(true);
            setIsChecking(false);
        } else {
            // Solicitar contraseña usando prompt
            setTimeout(() => {
                const password = prompt('🔐 Ingresa la contraseña para acceder al diagnóstico:');
                
                // Contraseñas válidas
                if (password === 'admin123' || password === 'diagnostic2025' || password === 'florentina') {
                    setIsAuthenticated(true);
                    sessionStorage.setItem('diagnostic_auth', 'true');
                } else if (password === null) {
                    // Usuario canceló
                    window.location.href = '/';
                    return;
                } else {
                    alert('❌ Contraseña incorrecta. Redirigiendo...');
                    window.location.href = '/';
                    return;
                }
                setIsChecking(false);
            }, 100);
        }
    }, []);

    // Si no está autenticado, mostrar pantalla de verificación
    if (isChecking) {
        return React.createElement('div', {
            style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'Arial, sans-serif',
                fontSize: '18px'
            }
        }, '🔍 Verificando acceso...');
    }

    if (!isAuthenticated) {
        return React.createElement('div', {
            style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'Arial, sans-serif',
                fontSize: '18px'
            }
        }, '❌ Acceso denegado');
    }

    const addLog = (message: string) => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
        console.log(message);
    };

    const testFirebaseConnection = async () => {
        setStatus('Testing...');
        setLogs([]);

        try {
            addLog('🔍 Starting Firebase connectivity test');
            
            // Show environment info
            const dbId = process.env.REACT_APP_FIREBASE_DATABASE_ID || 'default';
            addLog(`🗄️ Using database: ${dbId}`);
            addLog(`🏗️ Environment: ${process.env.NODE_ENV || 'development'}`);

            // Test 1: Enable network
            addLog('1️⃣ Enabling Firebase network...');
            await enableNetwork(db);
            addLog('✅ Network enabled successfully');

            // Test 2: Try to fetch a document that might not exist
            addLog('2️⃣ Testing document fetch...');
            const testDocRef = doc(db, 'boxes', 'test-connectivity');
            await getDoc(testDocRef);
            addLog('✅ Document fetch completed (even if document doesn\'t exist)');

            // Test 3: Test with timeout
            addLog('3️⃣ Testing with timeout...');
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Timeout')), 5000)
            );

            await Promise.race([
                getDoc(testDocRef),
                timeoutPromise
            ]);
            addLog('✅ Connection is responsive');

            setStatus('✅ All tests passed - Firebase is working correctly');

        } catch (error: any) {
            addLog(`❌ Test failed: ${error.message}`);
            addLog(`❌ Error code: ${error.code || 'No code'}`);
            setStatus(`❌ Tests failed: ${error.message}`);
        }
    };

    const forceNetworkReset = async () => {
        try {
            addLog('🔄 Resetting Firebase network...');
            await disableNetwork(db);
            addLog('📴 Network disabled');

            await new Promise(resolve => setTimeout(resolve, 1000));

            await enableNetwork(db);
            addLog('📶 Network re-enabled');
            setStatus('🔄 Network reset completed');
        } catch (error: any) {
            addLog(`❌ Network reset failed: ${error.message}`);
        }
    };

    // Función para cerrar sesión
    const handleLogout = () => {
        sessionStorage.removeItem('diagnostic_auth');
        window.location.href = '/';
    };

    // Crear la interfaz usando React.createElement para evitar problemas JSX
    return React.createElement('div', {
        style: { padding: '20px', maxWidth: '800px', margin: '0 auto' }
    }, 
        React.createElement('div', {
            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }
        },
            React.createElement('h1', null, '🔧 Firebase Diagnostic Tool'),
            React.createElement('button', {
                onClick: handleLogout,
                style: {
                    backgroundColor: '#dc3545',
                    color: 'white',
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }
            }, '🚪 Cerrar Sesión')
        ),

        React.createElement('div', {
            style: { marginBottom: '20px' }
        },
            React.createElement('h2', null, `Current Status: ${status}`)
        ),

        React.createElement('div', {
            style: { marginBottom: '20px' }
        },
            React.createElement('button', {
                onClick: testFirebaseConnection,
                style: {
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    marginRight: '10px',
                    cursor: 'pointer'
                }
            }, '🧪 Run Connectivity Test'),

            React.createElement('button', {
                onClick: forceNetworkReset,
                style: {
                    backgroundColor: '#FF9800',
                    color: 'white',
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }
            }, '🔄 Force Network Reset')
        ),

        React.createElement('div', {
            style: {
                backgroundColor: '#f5f5f5',
                padding: '15px',
                borderRadius: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '14px'
            }
        },
            React.createElement('h3', null, '📋 Diagnostic Logs:'),
            logs.length === 0 
                ? React.createElement('p', null, 'Click "Run Connectivity Test" to see logs...')
                : logs.map((log, index) => 
                    React.createElement('div', {
                        key: index,
                        style: { marginBottom: '5px' }
                    }, log)
                )
        ),

        React.createElement('div', {
            style: { marginTop: '20px', fontSize: '14px', color: '#666' }
        },
            React.createElement('h3', null, '💡 Tips para resolver "client is offline":'),
            React.createElement('ul', null,
                React.createElement('li', null, 'Verifica que tu conexión a internet sea estable'),
                React.createElement('li', null, 'Comprueba si hay bloqueo de firewall para Firebase'),
                React.createElement('li', null, 'Intenta con una red diferente (WiFi vs. móvil)'),
                React.createElement('li', null, 'Verifica que no hay adblockers bloqueando Firebase'),
                React.createElement('li', null, 'Revisa la consola del navegador para más detalles')
            )
        )
    );
};

export default DiagnosticPage;
